"use client";
import { useEffect, useRef, useState } from "react";
import useSWR from "swr";
import {
	fetchSupersetChartData,
	type SupersetChartMeta,
} from "../../../api/supersetApi";
import { pivotChartData } from "../lib/pivot";
import { BarChart } from "./charts/BarChart";
import { ChartSkeleton } from "./charts/ChartSkeleton";
import { DataTable } from "./charts/DataTable";
import { LineChart } from "./charts/LineChart";
import { PieChart } from "./charts/PieChart";
import { RecordList } from "./charts/RecordList";
import { StatPanel } from "./charts/StatPanel";

interface ChartCardProps {
	chart: SupersetChartMeta;
	marketId?: string | null;
	userAddress?: string | null;
	onLoadingChange?: (chartId: number, loading: boolean) => void;
}

type TableLayout = "data-table" | "stat-panel" | "record-list";

const IDENTITY_PATTERN = /profile|address/i;

// A wide table of metrics reads better as cards than as a table once it has
// either a clear per-record identity (wallet/profile -> RecordList) or a
// small, fixed category dimension (side: Yes/No -> StatPanel).
function chooseTableLayout(columns: string[]): TableLayout {
	const hasIdentity = columns.some((col) => IDENTITY_PATTERN.test(col));
	const hasSide = columns.includes("side");
	if (hasIdentity && columns.length > 5) return "record-list";
	if (hasSide && !hasIdentity && columns.length > 4) return "stat-panel";
	return "data-table";
}

// Per-chart overrides for layout/page size where the default heuristics
// don't fit:
const CHART_LAYOUT_OVERRIDES: Partial<
	Record<number, { layout?: TableLayout; pageSize?: number }>
> = {
	23: { layout: "record-list" }, // User Stats Snapshot
	25: { layout: "data-table", pageSize: 15 }, // Category Performance
	26: { layout: "data-table", pageSize: 10 }, // Holding Duration vs Profitability
	27: { layout: "data-table", pageSize: 10 }, // Confidence vs Win Rate
	28: { layout: "data-table", pageSize: 15 }, // Recent Trades
	43: { pageSize: 5 }, // Suspected Insiders
	55: { layout: "data-table", pageSize: 20 }, // Member Directory
	48: { layout: "data-table" }, // Side A — Top 10 Positions
	49: { layout: "data-table" }, // Side B — Top 10 Positions
	46: { layout: "data-table" }, // Side A — Top 5 in Loss
	47: { layout: "data-table" }, // Side B — Top 5 in Loss
	44: { layout: "data-table" }, // Side A — Top 5 in Profit
	45: { layout: "data-table" }, // Side B — Top 5 in Profit
};

function resolveSideLabel(
	data: { columns: string[]; rows: Record<string, unknown>[] } | undefined,
): string | null {
	if (!data?.columns.includes("side")) return null;
	const row = data.rows.find(
		(r) => typeof r.side === "string" && r.side.trim().length > 0,
	);
	return row ? String(row.side) : null;
}

function resolveChartTitle(title: string, sideLabel: string | null): string {
	if (!sideLabel) return title;
	return title.replace(/Side A/i, sideLabel).replace(/Side B/i, sideLabel);
}

function useIsNearViewport<T extends HTMLElement>() {
	const ref = useRef<T>(null);
	const [isNear, setIsNear] = useState(false);

	useEffect(() => {
		if (isNear || !ref.current) return;
		const el = ref.current;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					setIsNear(true);
					observer.disconnect();
				}
			},
			{ rootMargin: "400px" },
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [isNear]);

	return { ref, isNear };
}

export function ChartCard({
	chart,
	marketId,
	userAddress,
	onLoadingChange,
}: ChartCardProps) {
	const { ref, isNear } = useIsNearViewport<HTMLDivElement>();

	const { data, error, isLoading, isValidating } = useSWR(
		chart.supported && isNear
			? ["superset-chart", chart.id, marketId ?? "", userAddress ?? ""]
			: null,
		() => fetchSupersetChartData(chart.id, { marketId, userAddress }),
		{
			revalidateOnFocus: false,
			dedupingInterval: 15_000,
			keepPreviousData: true,
		},
	);

	useEffect(() => {
		onLoadingChange?.(chart.id, isValidating);
		return () => onLoadingChange?.(chart.id, false);
	}, [isValidating, chart.id, onLoadingChange]);

	const override = CHART_LAYOUT_OVERRIDES[chart.id];
	const tableLayout =
		chart.vizType === "table" && data
			? (override?.layout ?? chooseTableLayout(data.columns))
			: "data-table";
	const pageSize = override?.pageSize;
	const displayTitle = resolveChartTitle(chart.title, resolveSideLabel(data));

	const isWideTable =
		(chart.vizType === "table" && (data?.columns.length ?? 0) > 6) ||
		chart.vizType === "echarts_timeseries_line" ||
		chart.id === 28; // Recent Trades deserves full width

	return (
		<div
			ref={ref}
			className={`card bg-base-300 shadow-xl ${isWideTable ? "md:col-span-2" : ""}`}
		>
			<div className="card-body p-4 md:p-5">
				<div className="mb-2">
					<h3 className="text-xs md:text-sm font-bold text-base-content font-mono">
						{displayTitle}
					</h3>
					{chart.description && (
						<p className="text-[10px] text-base-content/50 font-mono mt-0.5 leading-snug">
							{chart.description}
						</p>
					)}
				</div>

				{!chart.supported && (
					<div className="text-xs font-mono text-base-content/50 py-6 text-center">
						This chart type isn't supported in native view yet — see it in
						Embedded mode.
					</div>
				)}

				{chart.supported && (!isNear || (isLoading && !data)) && (
					<ChartSkeleton />
				)}

				{chart.supported && isNear && error && !data && (
					<div className="text-xs font-mono text-error py-6 text-center">
						Couldn't load this chart.
					</div>
				)}

				{chart.supported && isNear && data && (
					<div className="relative">
						{isValidating && (
							<div className="absolute -top-1 right-0 flex items-center gap-1.5 text-[10px] font-mono text-primary/70 bg-base-300/90 px-1.5 py-0.5 rounded z-10">
								<span className="inline-block w-2.5 h-2.5 rounded-full border-2 border-primary/25 border-t-primary animate-spin" />
								updating…
							</div>
						)}
						<div
							className={`transition-opacity duration-300 ${isValidating ? "opacity-40" : "opacity-100"}`}
						>
							{chart.vizType === "table" && tableLayout === "record-list" && (
								<RecordList
									columns={data.columns}
									rows={data.rows}
									pageSize={pageSize}
								/>
							)}
							{chart.vizType === "table" && tableLayout === "stat-panel" && (
								<StatPanel
									columns={data.columns}
									rows={data.rows}
									titleColumn="side"
								/>
							)}
							{chart.vizType === "table" && tableLayout === "data-table" && (
								<DataTable
									columns={data.columns}
									rows={data.rows}
									pageSize={pageSize}
								/>
							)}
							{chart.vizType === "pie" && (
								<PieChart chart={pivotChartData(data)} />
							)}
							{chart.vizType === "echarts_timeseries_bar" && (
								<BarChart chart={pivotChartData(data)} />
							)}
							{chart.vizType === "echarts_timeseries_line" && (
								<LineChart chart={pivotChartData(data)} />
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
